import type { Story } from "../types/story";

/**
 * Pre-warms a story's assets while the cover weaves itself in, reporting real
 * progress (0..1) so the loading moment can be honest, not theatrical.
 *
 * What it warms, and why only this:
 *  - Every illustration (cover + one per scene) via `Image()` — images are what
 *    visibly POP when missing, so they're worth waiting for.
 *  - The FIRST scene's narration via a best-effort `fetch` into the HTTP cache —
 *    the opening line should start instantly; later scenes stream while the
 *    child reads the pages before them.
 *
 * Failure is never fatal: a 404'd image or an uncacheable audio file just
 * counts as done — the player already degrades gracefully without assets, and
 * bedtime must never block on a bad file. Content is only ever FETCHED from the
 * static bank, never generated.
 */
export function preloadStoryAssets(
  story: Story,
  assetBase: string,
  onProgress: (done: number, total: number) => void,
  signal?: AbortSignal
): Promise<void> {
  const resolve = (src: string) =>
    /^https?:\/\//.test(src) || src.startsWith("/") ? src : `${assetBase}${src}`;

  const imageSrcs: string[] = [];
  if (story.cover?.src) imageSrcs.push(resolve(story.cover.src));
  for (const scene of story.scenes) {
    if (scene.image?.src) imageSrcs.push(resolve(scene.image.src));
  }

  const firstSceneId = story.startSceneId;
  const firstAudio = story.scenes.find((s) => s.id === firstSceneId)?.audio?.src;

  const jobs: Promise<void>[] = [];

  for (const src of imageSrcs) {
    jobs.push(
      new Promise<void>((done) => {
        const img = new Image();
        img.decoding = "async";
        img.onload = () => done();
        img.onerror = () => done(); // missing art degrades gracefully later
        img.src = src;
        if (img.complete) done(); // already cached
      })
    );
  }

  if (firstAudio) {
    jobs.push(
      fetch(resolve(firstAudio), { signal })
        .then((res) => {
          // Drain the body so the response is fully cached, then discard it.
          return res.ok ? res.arrayBuffer().then(() => undefined) : undefined;
        })
        .catch(() => undefined)
    );
  }

  const total = jobs.length;
  if (total === 0) {
    onProgress(0, 0);
    return Promise.resolve();
  }

  let done = 0;
  onProgress(0, total);
  return Promise.all(
    jobs.map((j) =>
      j.then(() => {
        done += 1;
        if (!signal?.aborted) onProgress(done, total);
      })
    )
  ).then(() => undefined);
}
